<template>
    <div class="header-menu-wrapper" :class="[{'mobile': getMobileAppState}, getAppThemeMode]">
        <!-- Welcome message -->
        <div
        v-if="showWelcomeMessage"
        class="welcome-message">
            {{t('home.welcomeMessage1')}}, <span class="name">{{firstName}}</span><icon-hand/>
        </div>
        <dropdown-account-group
        v-if="getClientType === 'company'"
		class="account-group-dropdown"
		:class="{'with-notifications': notificationsMenuEntry}"
        :params="{ mobileDrawerTitle: t('menu.headerMenu.accountGroupsMobileDrawerTitle')}"
        :options="getAccountGroups"
        :selectedOption="selectedAccountGroup"
        @clicked="changeActive($event)"/>
        <!-- Space of dropdown-account-group when it doesnt exist -->
        <div v-else class="account-group-dropdown"></div>
		<!-- Search -->
		<header-search v-if="!getMobileAppState || showSearchOnHeaderMenu" :smallButton="showSearchOnHeaderMenu"/>
		<!-- Favorites -->
		<button-secondary v-if="favoriresMenuEntry" buttonStyle="round" buttonSize="small" icon="star" @clicked="navigateByPath(favoriresMenuEntry.link)"/>
		<!-- Notifications -->
		<button-secondary v-if="notificationsMenuEntry" buttonStyle="round" buttonSize="small" icon="letter" @clicked="navigateByPath(notificationsMenuEntry.link)"/>
		<!-- User Area -->
		<header-user-area :selectedMode="getAppThemeMode" @changeMode="[updateAppThemeMode($event), updateStatusBarStyle('lightContent'), saveThemeInUserRememberMeSession($event)]"/>
		<!-- Mode Selector -->
		<dropdown-mode v-if="!getMobileAppState" :selectedOption="getAppThemeModeId" @clicked="[updateAppThemeMode($event), updateStatusBarStyle('lightContent'), saveThemeInUserRememberMeSession($event)]"/>
    </div>
	<!-- Overlay Spinner -->
	<loader-generic v-if="showLoader" isOverlay/>
	<!-- Feedback Toasts -->
	<toast-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import IconHand from '@/assets/icons/hand.svg?inline'
import DropdownAccountGroup from '@/components/generic/dropdowns/accountGroup/AccountGroup'
import HeaderSearch from '@/components/generic/navigation/header/Search'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import HeaderUserArea from '@/components/generic/navigation/header/UserArea'
import DropdownMode from '@/components/generic/dropdowns/mode/Mode'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** props received
@showWelcomeMessage { Boolean } prop to show welcome message
@showSearchOnHeaderMenu { Boolean } prop to show search on header menu
*/
const props = defineProps({
    showWelcomeMessage: Boolean,
    showSearchOnHeaderMenu: Boolean
})

const { updateMenu } = mapActions(moduleMapper.MENU)
const { getMenu } = mapGetters(moduleMapper.MENU)
const { changeAccountGroupOfSession, requestPopupsList, saveThemeInUserRememberMeSession } = mapActions(moduleMapper.SESSION)
const { getAccountGroups, getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { updateAppThemeClientType, updateAppThemeMode, updateStatusBarStyle } = mapActions()
const { getMobileAppState, getAppThemeModeId, getClientType, getAppThemeMode } = mapGetters()

const showLoader = ref(false)
const showToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const selectedAccountGroup = computed(() => {
	return getAccountGroups.value.find(obj => obj.default).id
})

const isCompanySession = computed(() => {
    return getAccountGroups.value.find(obj => obj.default).type.toLowerCase() === 'company'
})

/* Changing current session client */
const changeActive = async clientId => {
	showLoader.value = true

	const payload = {
		data: { groupId: clientId, returnMenu: true },
		accountGroupType: getAccountGroups.value.find(obj => obj.id === clientId).type
	}

	try {
		const res = await changeAccountGroupOfSession(payload)
		updateAppThemeClientType(getAccountGroups.value.find(obj => obj.default).type.toLowerCase())
		updateMenu(res.data.data.menuItemList)
		requestPopupsList()
		if (!getMobileAppState.value) navigateByPath(routeList.HOME_PATH)
	} catch (error) {
		feedbackToastData.value = errorObjectConstructor(error)
		showToast.value = true
	} finally {
		showLoader.value = false
	}
}

const firstName = computed(() => {
    return getAllCustomerInfo.value.displayName.split(' ')[0]
})

const headerMenuEntries = computed(() => {
	return getMenu.value.filter(obj => obj.additionalData?.menuType === 'HEADER')
})

const favoriresMenuEntry = computed(() => {
	return headerMenuEntries.value.find(obj => obj.label === 'favorites')
})

const notificationsMenuEntry = computed(() => {
	return headerMenuEntries.value.find(obj => obj.label === 'notifications')
})
</script>

<style lang="scss" scoped>
.header-menu-wrapper {
	width: 100%;
    height: 64px;
	padding: 12px 24px;

	display: flex;
	gap: 16px;

	&:not(.mobile) {
		border-bottom: 1px solid var(--secondary-light-2);
	}

	&.mobile {
		height: 56px;
		margin-bottom: 16px;
		padding: 16px 16px 0 16px;
		gap: 16px;

		.account-group-dropdown {
            order: 2;
			max-width: calc(100% - 32px - 80px);

			&.with-notifications {
				max-width: calc(100% - 48px - 120px);
			}
		}

        .user-area-menu-wrapper {
            order: 1;
        }

        .welcome-message {
            width: auto;
            height: 40px;
            flex-shrink: 0;
            margin-right: 8px;

            display: flex;
            align-items: center;
            order: 2;
            gap: 4px;

            @include H5-Regular;
            color: var(--secondary-light-3);

            .name {
                text-transform: capitalize;
            }
        }

        :deep(.search-header-wrapper) {
            order: 3;
        }

        .button-secondary {
            order: 4;
        }

        @media only screen and (max-width: 360px) {
            gap: 8px;

            .account-group-dropdown {
                max-width: calc(100% - 16px - 80px);

                &.with-notifications {
                    max-width: calc(100% - 24px - 120px);
                }
            }

        }
	}

	.account-group-dropdown {
		max-width: 265px;
        width: 100%;
	}

	.mode-dropdown-wrapper {
		:deep(.dropdown-body) {
			left: unset;
			right: 0;

			z-index: 3;
		}
	}

    &.dark {
        .welcome-message {
            color: var(--secondary-dark-3);
        }
    }
}
</style>
